import mainStyle from "./mainStyle.module.scss";

import { ReactComponent as SwitchOnIcon } from "assets/images/icons/Toggle-On.svg";
import { ReactComponent as SwitchOffIcon } from "assets/images/icons/Toggle-Off.svg";
import { ReactComponent as UnverifiedIcon } from "assets/images/icons/icn_table_cell_Unverified.svg";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_table_cell_Bin.svg";
import { ReactComponent as BinHoverIcon } from "assets/images/icons/icn_table_cell_Bin_hover.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/icn_table_cell_Add.svg";
import { ReactComponent as AddHoverIcon } from "assets/images/icons/icn_table_cell_Add_hover.svg";
import { ReactComponent as AvatarNoneIcon } from "assets/images/icons/img_photo_none.svg";

import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { isEqual, isFunction } from "lodash";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";
import { useUserContext } from "contexts/UserContext";

// Component
import { StyledSelect } from "components/Select/searchWithSelect";
import { MenuSelect } from "components/Select/menuSelect";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import CollapseCard from "components/Collapse";
import ModalContainer from "components/ModalContainer";

// Fake data
const orgRoleHierarchy = [
  { orgRoleId: 5, orgRoleName: "Super Admin" },
  { orgRoleId: 6, orgRoleName: "Admin" },
  { orgRoleId: 7, orgRoleName: "Viewer" },
];

const siteRoleHierarchy = [
  { siteRoleId: 9, siteRoleName: "Admin" },
  { siteRoleId: 10, siteRoleName: "Viewer" },
];

const MemberUser = ({
  entId,
  orgId,
  isViewer,
  currentUserOrgRoleId,
  setCurrentUserOrgRoleId,
  allSitesName,
  originalRowData,
  setSelectedRowData,
  selectedRowData,
  setOpenDeleteAccountModal,
  setCurrentUserOrgRoleName,
  getInitials,
  formatStatus,
  closeDrawerCallback,
}) => {

  const theme = useTheme();
  const { userState } = useUserContext();
  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  // State
  const [isSwitched, setIsSwitched] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [isAddHovered, setIsAddHovered] = useState(false);
  const [updateUuid, setUpdateUuid] = useState(uuidv4());
  const [errorMessage, setErrorMessage] = useState("");
  const [lastSuperAdminModal, setLastSuperAdminModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [originUserId, setOriginUserId] = useState(null);

  const isLowerRoleThanMe =
    selectedRowData?.orgRoleId != null &&
    currentUserOrgRoleId != null &&
    currentUserOrgRoleId <= selectedRowData.orgRoleId;

  const roleNameToIdMap = useMemo(() => {
    const map = new Map();
    siteRoleHierarchy.forEach((r) => map.set(r.siteRoleName, r.siteRoleId));
    return map;
  }, [siteRoleHierarchy]);

  // Method
  const handleAccountStatusSwitch = () => {
    const newStatus = !isSwitched;
    setIsSwitched(newStatus);

    if (selectedRowData) {
      setSelectedRowData({
        ...selectedRowData,
        status: newStatus ? "active" : "inactive",
      });
    }
  };

  const handleChange = (index, field, value) => {
    if (field === "orgRole") {
      setSelectedRowData((prevData) => {
        if (
          value?.orgRoleName === "Admin" ||
          value?.orgRoleName === "Super Admin"
        ) {
          return {
            ...prevData,
            orgRoleName: value.orgRoleName,
            orgRoleId: value.orgRoleId,
            siteAccesses: [],
          };
        } else {
          return {
            ...prevData,
            orgRoleName: value.orgRoleName,
            orgRoleId: value.orgRoleId,
          };
        }
      });
    } else if (field === "role") {
      setSelectedRowData((prevData) => {
        const updated = [...prevData.siteAccesses];
        updated[index] = {
          ...updated[index],
          siteRoleName: value.siteRoleName,
          siteRoleId: value.siteRoleId,
        };
        return {
          ...prevData,
          siteAccesses: updated,
        };
      });
    } else if (field === "site") {
      setSelectedRowData((prevData) => {
        const updated = [...prevData.siteAccesses];
        updated[index] = {
          ...updated[index],
          siteName: value.siteName,
          siteId: value.siteId,
          siteRoleId: value.siteRoleId,
          siteRoleName: value.siteRoleName,
        };
        return {
          ...prevData,
          siteAccesses: updated,
        };
      });
    }
  };

  const handleOpenDeleteAccountModal = () => {
    setOpenDeleteAccountModal(true);
  };

  const handleDelete = (index) => {
    setSelectedRowData((prevData) => {
      const updatedAccess = prevData.siteAccesses.filter((_, i) => i !== index);
      return {
        ...prevData,
        siteAccesses: updatedAccess,
      };
    });
  };

  const handleAddClick = () => {
    setSelectedRowData((prevData) => {
      const updatedAccess = [
        ...prevData.siteAccesses,
        {
          siteName: "",
          siteId: "",
          siteRoleName: "",
          siteRoleId: null,
        },
      ];
      return {
        ...prevData,
        siteAccesses: updatedAccess,
      };
    });
  };

  const handleCloseModal = () => {
    setSelectedRowData(originalRowData);
  };

  const handleSaveChanges = () => {
    const siteAccesses = selectedRowData.siteAccesses.map(
      ({ siteId, siteRoleId }) => ({
        siteId,
        siteRoleId,
      })
    );

    const resourcePath = hasManageOrg && orgId
      ? "/ents/:entId/orgs/:orgId/users/:userId.update"
      : "/orgs/:orgId/users/:userId.update";

    const restfulParams = hasManageOrg && orgId
      ? { entId, orgId, userId: selectedRowData.id }
      : { orgId, userId: selectedRowData.id };

    const requestParams = {
      userId: selectedRowData.id,
      updates: {
        orgRoleId: selectedRowData.orgRoleId,
        siteAccesses,
        status: selectedRowData.status,
      },
    };

    setIsLoading(true);
    sendRequest(resourcePath, updateUuid, restfulParams, requestParams);
  };

  // Side Effect
  useEffect(() => {
    const fetchUserSetting = async () => {
      if (userState && process.env.REACT_APP_IS_MOCKUP === "false") {
        try {
          if (userState) {
            setOriginUserId(userState.id);
          }
        } catch (error) {
          console.error("Failed to fetch user setting:", error);
        }
      }
    };

    fetchUserSetting();
  }, [userState]);

  useEffect(() => {
    const updateResponse = getData(updateUuid);
    if (!updateResponse) return;

    setIsLoading(false);

    if (updateResponse?.code === 400) {
      setLastSuperAdminModal(true);
      setErrorMessage(updateResponse?.message);
    } else {
      if (selectedRowData.id === originUserId) {
        setCurrentUserOrgRoleName(selectedRowData.orgRoleName);
        setCurrentUserOrgRoleId(selectedRowData.orgRoleId);
      }

      if (isFunction(closeDrawerCallback)) {
        closeDrawerCallback();
      }
    }
  }, [dataMap]);

  useEffect(() => {
    if (selectedRowData) {
      setIsSwitched(selectedRowData.status === "active");
    } else {
      setIsSwitched(false);
    }
  }, [selectedRowData]);

  const isSaveDisabled =
    selectedRowData?.siteAccesses?.some((site) => !site || !site.siteName || !site.siteRoleName) ||
    (selectedRowData?.orgRoleId === 7 && selectedRowData?.siteAccesses?.length === 0);

  return (
    <>
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress sx={{ color: theme.palette.primary.mainHover }} />
        </Box>
      )}

      <Box
        sx={{
          paddingLeft: "25px",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {selectedRowData && (
          <>
            {selectedRowData.avatar ? (
              <img
                src={selectedRowData.avatar}
                alt="User"
                className={mainStyle["avatar-with-photo"]}
              />
            ) : selectedRowData.name ? (
              <div className={mainStyle["avatar-without-photo-has-name"]}>
                {getInitials(selectedRowData.name)}
              </div>
            ) : (
              <div className={mainStyle["avatar-without-photo"]}>
                <AvatarNoneIcon />
              </div>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {selectedRowData.name && (
                <span
                  style={{
                    fontWeight: theme.typography.customFontWeight[3],
                    fontSize: theme.typography.customFontSize[5],
                    color: theme.palette.primary.greyPrimary,
                  }}
                >
                  {selectedRowData.name}
                </span>
              )}
              {selectedRowData.status && (
                <span
                  style={{
                    color:
                      selectedRowData.status === "pending" ||
                        selectedRowData.status === "expired"
                        ? theme.palette.primary.redPrimary
                        : selectedRowData.status === "active"
                          ? theme.palette.primary.greenPrimary
                          : selectedRowData.status === "inactive"
                            ? theme.palette.primary.greySecound
                            : theme.palette.primary.greyPrimary,
                    border: `1.5px solid ${selectedRowData.status === "pending" ||
                      selectedRowData.status === "expired"
                      ? theme.palette.primary.redPrimary
                      : selectedRowData.status === "active"
                        ? theme.palette.primary.greenPrimary
                        : selectedRowData.status === "inactive"
                          ? theme.palette.primary.greySecound
                          : theme.palette.primary.greyPrimary
                      }`,
                    padding: "4px 12px",
                    width: "91px",
                    height: "27px",
                    gap: "10px",
                    borderRadius: theme.shape.customBorderRadius[7],
                    fontSize: theme.typography.customFontSize[0],
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    display: "flex",
                  }}
                >
                  {selectedRowData.status === undefined
                    ? "-"
                    : formatStatus(selectedRowData.status)}
                </span>
              )}
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 2,
          boxSizing: "border-box",
          gap: "20px",
          position: "relative",
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              pr: 2,
            }}
          >
            {selectedRowData?.lastLoginTime && (
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greySecound,
                  fontFamily: theme.typography.fontFamily,
                  whiteSpace: "nowrap",
                }}
              >
                Last Login:
                <span
                  style={{
                    color: theme.palette.primary.mainHover,
                    marginLeft: 4,
                  }}
                >
                  {dayjs
                    .unix(selectedRowData.lastLoginTime)
                    .format("YYYY/MM/DD HH:mm:ss")}
                </span>
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
              boxSizing: "border-box",
            }}
          >
            <CollapseCard title="User Information">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {selectedRowData && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        className={
                          mainStyle["user-management-modal-typography"]
                        }
                      >
                        Account Status
                      </Typography>
                      <Box
                        onClick={
                          isViewer || !isLowerRoleThanMe
                            ? undefined
                            : handleAccountStatusSwitch
                        }
                        sx={{
                          cursor:
                            isViewer || !isLowerRoleThanMe
                              ? "not-allowed"
                              : "pointer",
                          display: "flex",
                        }}
                      >
                        {isSwitched ? (
                          <SwitchOnIcon
                            style={{
                              filter:
                                isViewer || !isLowerRoleThanMe
                                  ? "grayscale(100%) opacity(0.5)"
                                  : "none",
                              pointerEvents: "none",
                            }}
                          />
                        ) : (
                          <SwitchOffIcon
                            style={{
                              filter:
                                isViewer || !isLowerRoleThanMe
                                  ? "grayscale(100%) opacity(0.5)"
                                  : "none",
                              pointerEvents: "none",
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        className={
                          mainStyle["user-management-modal-typography"]
                        }
                      >
                        Name
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: theme.typography.customFontSize[2],
                          fontFamily: theme.typography.fontFamily,
                          fontWeight: theme.typography.customFontWeight[1],
                        }}
                      >
                        {selectedRowData.name || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        className={
                          mainStyle["user-management-modal-typography"]
                        }
                      >
                        Organization Role
                      </Typography>

                      {isViewer || !isLowerRoleThanMe ? (
                        <Typography
                          sx={{
                            fontSize: theme.typography.customFontSize[2],
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: theme.typography.customFontWeight[1],
                          }}
                        >
                          {selectedRowData.orgRoleName}
                        </Typography>
                      ) : (
                        <MenuSelect
                          value={selectedRowData.orgRoleName}
                          onChange={(opt) => handleChange(null, "orgRole", opt)}
                          options={(() => {
                            if (
                              !currentUserOrgRoleId ||
                              typeof currentUserOrgRoleId !== "number"
                            )
                              return [];

                            return orgRoleHierarchy
                              .filter(
                                (role) => role.orgRoleId >= currentUserOrgRoleId
                              )
                              .map((role) => ({
                                label: role.orgRoleName,
                                value: role.orgRoleName,
                                orgRoleId: role.orgRoleId,
                                orgRoleName: role.orgRoleName,
                              }));
                          })()}
                          width={308}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        display:
                          selectedRowData.orgRoleName === "Viewer"
                            ? "block"
                            : "flex",
                        gap: isLowerRoleThanMe ? 10 : 9.5,
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.primary.black,
                          fontSize: theme.typography.customFontSize[2],
                          fontWeight: theme.typography.customFontWeight[0],
                          letterSpacing: "0.16px",
                          fontFamily: theme.typography.fontFamily,
                          mb: selectedRowData.orgRoleName === "Viewer" ? 1 : 0,
                        }}
                      >
                        Site Privilege
                      </Typography>

                      {selectedRowData.orgRoleName === "Super Admin" ||
                        selectedRowData.orgRoleName === "Admin" ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontFamily: theme.typography.fontFamily,
                            fontSize: theme.typography.customFontSize[2],
                            color: theme.palette.primary.greyPrimary,
                            fontWeight: theme.typography.customFontWeight[1],
                          }}
                        >
                          All Sites
                        </Box>
                      ) : (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              width: "100%",
                              mt: 3,
                            }}
                          >
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "left",
                                color: theme.palette.primary.greyThird,
                                fontSize: theme.typography.customFontSize[3],
                                fontWeight:
                                  theme.typography.customFontWeight[2],
                                fontFamily: theme.typography.fontFamily,
                              }}
                            >
                              Site
                            </Box>
                            <Box
                              sx={{
                                flex: 1,
                                marginRight: "20px",
                                display: "flex",
                                justifyContent: "left",
                                color: theme.palette.primary.greyThird,
                                fontSize: theme.typography.customFontSize[3],
                                fontWeight:
                                  theme.typography.customFontWeight[2],
                                fontFamily: theme.typography.fontFamily,
                              }}
                            >
                              Role
                            </Box>
                          </Box>
                          <Divider
                            sx={{
                              marginTop: "10px",
                              borderColor: theme.palette.primary.greyThread,
                              borderWidth: 1,
                            }}
                          />

                          {selectedRowData.siteAccesses?.map((site, index) => (
                            <Box
                              sx={{
                                marginTop: "20px",
                                display: "flex",
                                width: "100%",
                              }}
                              key={index}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  justifyContent: "left",
                                  color: theme.palette.primary.greyThird,
                                  fontSize: theme.typography.customFontSize[3],
                                  fontWeight:
                                    theme.typography.customFontWeight[2],
                                  fontFamily: theme.typography.fontFamily,
                                }}
                              >
                                {isViewer ? (
                                  <Typography
                                    sx={{
                                      fontFamily: theme.typography.fontFamily,
                                      fontSize:
                                        theme.typography.customFontSize[2],
                                      color: theme.palette.primary.greyPrimary,
                                    }}
                                  >
                                    {site.siteName}
                                  </Typography>
                                ) : (
                                  <StyledSelect
                                    value={site.siteName}
                                    onChange={(opt) =>
                                      handleChange(index, "site", opt)
                                    }
                                    options={allSitesName.map((s) => ({
                                      ...s,
                                      value: s.siteName,
                                      label: s.siteId,
                                    }))}
                                    disabledOptions={selectedRowData.siteAccesses
                                      .filter((s, idx) => idx !== index)
                                      .map((s) => s.siteName)}
                                    width={210}
                                    placeholder="Select Site"
                                  />
                                )}
                              </Box>

                              <Box
                                sx={{
                                  flex: 1,
                                  display: "flex",
                                  justifyContent: "left",
                                  color: theme.palette.primary.greyThird,
                                  fontSize: theme.typography.customFontSize[3],
                                  fontWeight:
                                    theme.typography.customFontWeight[2],
                                  fontFamily: theme.typography.fontFamily,
                                  alignItems: "center",
                                }}
                              >
                                {isViewer ? (
                                  <Typography
                                    sx={{
                                      fontFamily: theme.typography.fontFamily,
                                      fontSize:
                                        theme.typography.customFontSize[2],
                                      color: theme.palette.primary.greyPrimary,
                                      marginLeft: "-10px",
                                    }}
                                  >
                                    {site.siteRoleName}
                                  </Typography>
                                ) : (
                                  <>
                                    <MenuSelect
                                      value={site.siteRoleName}
                                      onChange={(opt) =>
                                        handleChange(index, "role", opt)
                                      }
                                      options={(() => {
                                        if (!site.siteName) return [];

                                        const matchedOrg = allSitesName.find(
                                          (org) =>
                                            org.siteName === site.siteName
                                        );

                                        if (!matchedOrg) return [];

                                        const maxRoleId = roleNameToIdMap.get(
                                          matchedOrg.siteRoleName
                                        );

                                        if (typeof maxRoleId !== "number")
                                          return [];

                                        return siteRoleHierarchy
                                          .filter(
                                            (role) =>
                                              role.siteRoleId >= maxRoleId
                                          )
                                          .map((role) => ({
                                            label: role.siteRoleName,
                                            value: role.siteRoleName,
                                            siteRoleId: role.siteRoleId,
                                            siteRoleName: role.siteRoleName,
                                          }));
                                      })()}
                                      width={index === 0 ? 230 : 200}
                                      placeholder="Select Role"
                                    />

                                    {index > 0 && (
                                      <Button
                                        sx={{
                                          padding: 0,
                                          minWidth: 0,
                                          backgroundColor:
                                            theme.palette.primary.transparent,
                                          boxShadow: "none",
                                          "&:hover": {
                                            backgroundColor:
                                              theme.palette.primary.transparent,
                                          },
                                        }}
                                        onClick={() => handleDelete(index)}
                                      >
                                        <UnverifiedIcon />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Box>
                          ))}

                          {!isViewer && (
                            <Box
                              sx={{
                                marginTop: "20px",
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <OutlineRectangleButton
                                onMouseEnter={() => setIsAddHovered(true)}
                                onMouseLeave={() => setIsAddHovered(false)}
                                startIcon={
                                  isAddHovered ? <AddHoverIcon /> : <AddIcon />
                                }
                                width="54px"
                                height="28px"
                                onClick={handleAddClick}
                              >
                                Add
                              </OutlineRectangleButton>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </CollapseCard>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pl: 1,
              pr: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
              }}
            >
              {!isViewer && isLowerRoleThanMe && (
                <Button
                  onMouseEnter={() => setIsDeleteHovered(true)}
                  onMouseLeave={() => setIsDeleteHovered(false)}
                  startIcon={isDeleteHovered ? <BinHoverIcon /> : <BinIcon />}
                  onClick={handleOpenDeleteAccountModal}
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: theme.typography.customFontSize[3],
                    fontFamily: theme.typography.fontFamily,
                    backgroundColor: theme.palette.primary.transparent,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.transparent,
                      color: theme.palette.primary.mainHover,
                    },
                    "&:active": {
                      backgroundColor: theme.palette.primary.transparent,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  Delete User
                </Button>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  color: theme.palette.primary.greySecound,
                  fontFamily: theme.typography.fontFamily,
                  whiteSpace: "nowrap",
                }}
              >
                Joined On:
                <span style={{ color: theme.palette.primary.mainHover }}>
                  {dayjs
                    .unix(selectedRowData?.timeOfAuth)
                    .format("YYYY/MM/DD HH:mm:ss")}
                </span>
              </Typography>
            </Box>
          </Box>
        </Box>
        {
          (!isViewer && isLowerRoleThanMe)
          && !isEqual(selectedRowData, originalRowData)
          && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'primary.lightBlue',
                width: '530px',
                height: '64px',
                gap: 2,
              }}
            >
              <OutlineButton
                onClick={handleCloseModal}
                sx={{ marginRight: "20px" }}
                width="100px"
                height="32px"
              >
                Cancel
              </OutlineButton>
              <SolidButton
                onClick={handleSaveChanges}
                disabled={isSaveDisabled}
                width="100px"
                height="32px"
              >
                Apply
              </SolidButton>
            </Box>
          )
        }
      </Box>

      <ModalContainer display={lastSuperAdminModal} modalWidth="500px">
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Action denied
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
                fontFamily: theme.typography.fontFamily,
                marginTop: "20px",
              }}
            >
              You are currently the last <strong>Super Admin</strong>. <br />
              Please assign another user before leaving or changing your role.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <SolidButton
              sx={{ marginTop: "30px" }}
              onClick={() => setLastSuperAdminModal(false)}
              width="100px"
              height="32px"
            >
              OK
            </SolidButton>
          </Box>
        </Box>
      </ModalContainer>
    </>
  );
};
export default MemberUser;
